// TIP-615 — the membership step of the checkout, copy and figures.
// Design: `Checkout Flow.dc.html` sections 01 / 1a-1f (desktop) and 3a-3b
// (mobile). The same shell as the buy-briefs step, a different product.
// WHERE THE PRICES COME FROM, AND THE PROBLEM WITH THAT
// `MEMBERSHIP_PLANS` in membership/membershipContent.js — the source the
// dashboard's own membership screen uses. Deliberately NOT a new constant
// here: the figure already exists in two places (that file and
// PaymentScreen's `PLAN_PRICES`), and a third copy is how it drifts again.
// It is still a hardcoded price, and TIP-615 says plainly that it should not
// be: "No hardcoded membership price anywhere in the new components" and
// "the modal reads the price from the backend rather than a constant, so this
// cannot drift a fourth time". There is no endpoint that exposes membership
// prices, so this reads the least-bad existing source and is marked for
// replacement the moment there is one.
// Worse, the figure itself is disputed. Every frontend copy says AED 99 a
// month; TIP-615 says the real price is AED 99.90. Only the Stripe Price
// object behind STRIPE_PRICE_ID_MEMBERSHIP_MONTHLY can settle it. Until it is
// settled the monthly card may be wrong by 90 fils, and Stripe will charge
// its own figure on step 2 regardless of what we draw.
import { MEMBERSHIP_PLANS } from "../membership/membershipContent";

/** Whole AED in the shared content module -> integer fils here. */
const toFils = (aed) => Math.round(Number(aed) * 100);

/** The plan's price in fils, excluding tax. Stripe adds whatever applies. */
export const planQuote = (planId) => {
  const plan = MEMBERSHIP_PLANS[planId];
  if (!plan) return null;
  return { netFils: toFils(plan.price) };
};

/** The two cards, in the order the design draws them: annual first. */
export const MEMBERSHIP_PLAN_ORDER = ["yearly", "monthly"];

export const MEMBERSHIP_COPY = {
  eyebrow: "TREVEX membership",
  /** "Membership for Al Futtaim Logistics LLC" */
  title: (company) => (company ? `Membership for ${company}` : "TREVEX membership"),
  sub: "One membership. Everyone on your team is covered.",
  close: "Close",

  plans: {
    yearly: {
      name: "Annual",
      price: (net) => `${net} / year`,
      note: "Two months free",
    },
    monthly: {
      name: "Monthly",
      price: (net) => `${net} / month`,
      note: "Cancel any time.",
    },
  },

  /* The navy number strip. Three figures, no ticks — the numbers carry the argument and the list below confirms the rest exists. These describe what MEMBERSHIP... */
  quota: [
    { value: "30", label: "Briefs a month, up from 20" },
    { value: "None", label: "Weekly cap, instead of 5" },
    { value: null, label: "AED per extra brief, 10% off" },
  ],

  benefits: [
    "Payment endorsements",
    "Verification renewal included",
    "Business matching",
    "Partner programme rates",
  ],

  /** "Membership, annual" / "Membership, monthly" */
  lineLabel: (planId) => `Membership, ${planId === "yearly" ? "annual" : "monthly"}`,
  /** Drawn from the live rate: "VAT 5%" in the UAE, "VAT 0%" outside it. */
  /** Tax is Stripe's — see buyBriefsCopy.excludingTax. */
  totalLabel: "Total",
  /** Monthly says "today", because there is another one next month. */
  totalTodayLabel: "Total today",
  excludingTax: "(excluding tax)",
  /* The monthly plan recurs, and a recurring charge has to say so. */
  // The amount is left out: it is the tax-inclusive figure, which is Stripe's.
  renewalNote: "Charged monthly until you cancel.",

  /* VAT before the billing address exists. */
  vatNote: "Final VAT is set by the billing address you enter at payment.",

  cta: "Continue to payment",
  ctaHelper: "Card details are entered on the next step, with Stripe.",
  notNow: "Not now",

  // ---- the bridge and step 2 ---------------------------------------------
  bridgeTitle: "Setting up secure payment",
  bridgeSub: "This takes a second. Do not refresh.",
  bridgeNote: "Talking to Stripe",

  orderEyebrow: "Your order",
  orderTitle: (planId) => `Membership, ${planId === "yearly" ? "annual" : "monthly"}`,
  /** The design adds a renewal date here; we have none, so just the company. */
  orderSub: (company) => company || "",
  totalShort: "Total",
  /** "30 briefs a month. No weekly cap. Extras at AED 134.10." */
  orderNote: (memberUnit) =>
    memberUnit
      ? `30 briefs a month. No weekly cap. Extras at ${memberUnit}.`
      : "30 briefs a month. No weekly cap.",
  invoiceTo: (email) => `Invoice goes to ${email} after payment.`,
  back: "Change plan",

  paymentTitle: "Payment",
  paymentSub: "Stripe handles the card. We never see the number.",
  stripeFooter: "Payments are processed by Stripe. TREVEX does not store your card.",

  errorTitle: "We could not start checkout",
  errorBody:
    "Nothing has been charged and your plan is still selected. Try again, or come back in a minute.",
  tryAgain: "Try again",

  /* Design 1f, minus the figures nothing reports. */
  successEyebrow: "Payment received",
  successTitle: "Membership is active",
  successBody: (company) =>
    company
      ? `${company} is a TREVEX member. Everyone on your team has it now.`
      : "Everyone on your team has it now.",
  successAllowance: "Your brief allowance is now 30 a month with no weekly cap.",
  successAction: "Back to briefs",

  /**
   * The 409 from verified-checkout. A Member reaching this modal is told,
   * never sold to twice — TIP-615 requires the conflict path.
   */
  alreadyMemberTitle: "This business is already a member",
  alreadyMemberBody:
    "Nothing has been charged. Your membership is active, so the extra briefs are already at the member price.",
  alreadyMemberAction: "Back to briefs",
};

export default MEMBERSHIP_COPY;
